import React, { useEffect, useState } from "react";
import "../Beneficiary/BeneficiaryStyle.scss";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getRelation } from "../../Redux/PersonalAccidentRedux";
import { useTranslation } from "react-i18next";
import { updateLeadsLife } from "../../Redux/LogsRedux/Life/LifeLeadsRedux";

const AddBeneficiary = ({ setShowConfirm, planDetails }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // const { planDetails } = location.state || {};

  const [beneficiaries, setBeneficiaries] = useState([
    { name: "", relation: "Spouse", benefit_percentage: "" },
  ]);

  const [relations, setRelations] = useState();
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);

  useEffect(() => {
    if (beneficiaries.length === 1) {
      const onlyBeneficiary = beneficiaries[0];

      // Auto-set percentage = 100 for a single beneficiary
      if (Number(onlyBeneficiary.benefit_percentage) !== 100) {
        setBeneficiaries([{ ...onlyBeneficiary, benefit_percentage: "100" }]);
        return; // Prevent re-validation until state updates
      }

      const { name, relation, benefit_percentage } = onlyBeneficiary;
      const isValid =
        name.trim() &&
        relation.trim() &&
        benefit_percentage.trim() &&
        Number(benefit_percentage) === 100;
      setIsButtonEnabled(isValid);
    } else if (beneficiaries.length > 1) {
      const allFilled = beneficiaries.every(
        (b) =>
          b.name.trim() && b.relation.trim() && b.benefit_percentage.trim(),
      );
      const totalPercentage = beneficiaries.reduce(
        (sum, b) => sum + Number(b.benefit_percentage || 0),
        0,
      );

      setIsButtonEnabled(allFilled && totalPercentage === 100);
    } else {
      setIsButtonEnabled(false);
    }
  }, [beneficiaries]);

  useEffect(() => {
    if (planDetails?.beneficiary) {
      setBeneficiaries(planDetails?.beneficiary);
    }
  }, [planDetails?.beneficiary]);

  const handleChange = (index, field, value) => {
    const updated = [...beneficiaries];

    if (field === "benefit_percentage") {
      const normalized = value.replace(/[^0-9]/g, "");
      const clamped = Math.min(100, Number(normalized || 0));
      updated[index][field] = normalized === "" ? "" : String(clamped);

      if (beneficiaries.length > 1 && normalized !== "") {
        const remaining = 100 - clamped;
        const othersCount = beneficiaries.length - 1;
        const baseShare = Math.floor(remaining / othersCount);
        let remainder = remaining % othersCount;

        updated.forEach((beneficiary, idx) => {
          if (idx !== index) {
            const share = baseShare + (remainder > 0 ? 1 : 0);
            beneficiary.benefit_percentage = String(share);
            remainder -= 1;
          }
        });
      }
    } else {
      updated[index][field] = value;
    }

    setBeneficiaries(updated);
  };

  const addBeneficiary = () => {
    setBeneficiaries([
      ...beneficiaries,
      { name: "", relation: "", benefit_percentage: "" },
    ]);
  };

  const removeBeneficiary = (index) => {
    if (beneficiaries.length > 1) {
      const updated = beneficiaries.filter((_, i) => i !== index);
      setBeneficiaries(updated);
    }
  };

  const handleNext = () => {
    const payload = {
      transId: localStorage.getItem("LIFE_LEAD_ID"),
      beneficiary: beneficiaries,
    };
    dispatch(updateLeadsLife(payload)).then((response) => {
      if (response.success) {
        const updatedData = {
          ...planDetails,
          beneficiary: beneficiaries,
        };
        navigate("/personal-information", {
          state: { planDetails: updatedData },
        });
      }
    });
  };

  useEffect(() => {
    dispatch(getRelation()).then((result) => {
      if (result.success) {
        setRelations(result?.data);
      }
    });
  }, [dispatch]);

  const handleCancel = () => {
    setShowConfirm(false);
  };

  return (
    <div className="beneficiary-card">
      <h2 className="title fs-5">{t("Add Beneficiary")}</h2>

      <div className="beneficiary-table-wrapper">
        <table className="beneficiary-table">
          <thead>
            <tr>
              <th>{t("Beneficiary")}</th>
              <th>{t("Name")}</th>
              <th>{t("Percentage")}</th>
              <th>{t("Relation")}</th>
              <th className="action-column">{t("Action")}</th>
            </tr>
          </thead>
          <tbody>
            {beneficiaries.map((b, i) => (
              <tr key={i}>
                <td className="beneficiary-label">
                  {t("Beneficiary")} {i + 1}
                </td>
                <td>
                  <input
                    type="text"
                    placeholder={t("Enter Name")}
                    value={b.name}
                    onChange={(e) => handleChange(i, "name", e.target.value)}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    placeholder={t("Enter percentage")}
                    value={b.benefit_percentage}
                    disabled={beneficiaries.length === 1}
                    onChange={(e) =>
                      handleChange(i, "benefit_percentage", e.target.value)
                    }
                  />
                </td>
                <td>
                  <select
                    value={b.relation}
                    onChange={(e) => handleChange(i, "relation", e.target.value)}
                  >
                    <option value="">{t("Select Relation")}</option>
                    {relations?.map((rel) => (
                      <option key={rel} value={rel}>
                        {rel}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="action-column">
                  {beneficiaries.length > 1 ? (
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => removeBeneficiary(i)}
                      aria-label={`${t("Remove")} ${t("Beneficiary")} ${i + 1}`}
                    >
                      <i className="bi bi-dash-circle-fill"></i>
                    </button>
                  ) : (
                    <span className="remove-placeholder">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="add-beneficiary-btn-wrap">
        <button type="button" className="btn btn-primary w-30" onClick={addBeneficiary}>
          {t("Add Beneficiary")}
        </button>
      </div>

      <div className="actions">
        <button type="button" className="btn btn-primary" onClick={handleCancel}>
          {t("Cancel")}
        </button>
        <button
          type="button"
          className="btn btn-primary px-4"
          onClick={handleNext}
          disabled={!isButtonEnabled}
        >
          {t("Next")}
        </button>
      </div>
    </div>
  );
};

export default AddBeneficiary;
